import React, { useState } from 'react';
import { FormControlLabel, Grid, Radio, RadioGroup, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useTranslation } from 'react-i18next';
import { usePostHog } from 'posthog-js/react';
import useSWR from 'swr';
import { OrderData, OrderPageData, VoucherOut, VoucherService, VoucherSoldTypes, VoucherType } from '../../../../api';
import ConfirmationDialog from '../../../../common/ui/ConfimationDialog';
import { CopyLink } from '../../../../common/ui/CopyLink';
import { voucherFetcher } from '../../../../vouchers/VouchersTable';
import { useLanguage } from '../../../../common/GeneralUtils';
import { usePriceFormatter } from '../../../../common/usePriceFormatter';

type UseVoucherDialogsParams = {
  order: Partial<OrderData>;
  orderPage: OrderPageData;
  closeEditOrderDialog: () => void;
};

type UseVoucherDialogsResult = {
  dialogNodes: React.ReactNode;
  setPaymentLink: (link: string | undefined) => void;
  setVoucher: (voucher: VoucherOut) => void;
  setVoucherConfirmationDialogIsOpen: (open: boolean) => void;
  setConvertToVoucherDialogIsOpen: (open: boolean) => void;
  setConvertedToVoucher: (converted: boolean) => void;
};

export const useVoucherDialogs = ({
  order,
  orderPage,
  closeEditOrderDialog,
}: UseVoucherDialogsParams): UseVoucherDialogsResult => {
  const { t } = useTranslation();
  const posthog = usePostHog();
  const language = useLanguage();
  const priceFormatter = usePriceFormatter();
  const [paymentLink, setPaymentLink] = useState<string | undefined>(undefined);
  const [voucher, setVoucher] = useState<VoucherOut | undefined>(undefined);
  const [voucherConfirmationDialogIsOpen, setVoucherConfirmationDialogIsOpen] = useState(false);
  const [convertToVoucherDialogIsOpen, setConvertToVoucherDialogIsOpen] = useState(false);
  const [convertedToVoucher, setConvertedToVoucher] = useState(false);
  const [convertVoucherType, setConvertVoucherType] = useState(VoucherType.EXPERIENCE);
  const [sendingVoucherEmail, setSendingVoucherEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const { mutate: mutateVouchers } = useSWR(
    { search: undefined, page: 1, perPage: 8, opId: 'getVouchers', filterState: undefined },
    voucherFetcher,
  );

  const link = voucher?.redemption_link || '';
  const soldTypes = orderPage.effective_vouchers_config?.sold_types;
  const canConvertToMonetary = soldTypes === VoucherSoldTypes.MONETARY || soldTypes === VoucherSoldTypes.BOTH;

  const dialogNodes = (
    <>
      <ConfirmationDialog
        title={t('calendar.edit_order.paymentLink')}
        content={<CopyLink link={paymentLink} orderPageId={orderPage.id} />}
        isOpen={!!paymentLink}
        onClose={() => {
          setPaymentLink(undefined);
          closeEditOrderDialog();
        }}
        hideCancelButton
        confirmButtonColor={'primary'}
        ctaText={t('vouchers.confirmation_dialog.cta')}
      />
      <ConfirmationDialog
        title={t('vouchers.confirmation_dialog.title')}
        content={
          <Grid container flexDirection="column">
            <CopyLink link={link} orderPageId={orderPage.id} />
            <Grid item hidden={!convertedToVoucher} px={4} mt={4}>
              <LoadingButton
                disabled={emailSent}
                fullWidth
                variant="outlined"
                loading={sendingVoucherEmail}
                onClick={async () => {
                  setSendingVoucherEmail(true);
                  await VoucherService.sendVoucher({ voucher_id: voucher!.id });
                  setEmailSent(true);
                  setSendingVoucherEmail(false);
                }}>
                {emailSent ? t('vouchers.confirmation_dialog.sent') : t('vouchers.confirmation_dialog.sendToCustomer')}
              </LoadingButton>
            </Grid>
          </Grid>
        }
        isOpen={voucherConfirmationDialogIsOpen}
        onClose={() => {
          setVoucherConfirmationDialogIsOpen(false);
          closeEditOrderDialog();
          if (mutateVouchers && voucher) {
            void mutateVouchers([voucher]);
          }
        }}
        hideCancelButton
        confirmButtonColor={'primary'}
        ctaText={t('vouchers.confirmation_dialog.cta')}
      />
      <ConfirmationDialog
        title={t('vouchers.convertDialog.title')}
        content={
          <Grid container flexDirection="column" gap={1}>
            <Typography>{t('vouchers.convertDialog.areYouSure')}</Typography>
            {canConvertToMonetary && (
              <>
                <Typography fontWeight={700}>{t('vouchers.convertDialog.chooseType')}</Typography>
                <RadioGroup
                  value={convertVoucherType}
                  onChange={(event) => setConvertVoucherType(event.target.value as VoucherType)}>
                  <FormControlLabel
                    value={VoucherType.EXPERIENCE}
                    control={<Radio size="small" />}
                    label={t('vouchers.convertDialog.experienceOption', { label: orderPage.label[language] })}
                  />
                  <FormControlLabel
                    value={VoucherType.MONETARY}
                    control={<Radio size="small" />}
                    label={
                      <Grid container gap={0.5} alignItems="center">
                        <Typography>{t('vouchers.convertDialog.monetaryOption')}</Typography>
                        {priceFormatter(order.price!, false, 'body1')}
                      </Grid>
                    }
                  />
                </RadioGroup>
              </>
            )}
          </Grid>
        }
        isOpen={convertToVoucherDialogIsOpen}
        onClose={async (actionConfirmed: boolean) => {
          if (actionConfirmed) {
            posthog.capture('Button clicked', { name: 'Convert to voucher' });
            const converted = await VoucherService.convertToVoucher({
              order_id: order.id!,
              voucher_type: convertVoucherType,
            });
            setVoucher(converted);
            setConvertedToVoucher(true);
            setVoucherConfirmationDialogIsOpen(true);
          }
          setConvertToVoucherDialogIsOpen(false);
        }}
        loadingButton
        confirmButtonColor={'primary'}
        ctaText={t('vouchers.convertDialog.cta')}
      />
    </>
  );

  return {
    dialogNodes,
    setPaymentLink,
    setVoucher,
    setVoucherConfirmationDialogIsOpen,
    setConvertToVoucherDialogIsOpen,
    setConvertedToVoucher,
  };
};
